import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { Button, ErrorNote, Spinner, ui } from '@/components/ui';
import { authService, type SocialProvider } from '@/services/api';
import { sessionKey, useSession } from './session';
import { SocialSignIn } from './SocialSignIn';
import { env } from '@/utils/env';
import { MfaLoginStep } from '@/features/mfa/MfaLoginStep';
import s from './AuthPages.module.css';

const BENEFITS = [
  'Reply to every review from one inbox',
  'Invite customers after each order',
  'Track your rating, volume and response rate',
];

export function AuthCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className={s.wrap}>
      <section className={s.pitch} aria-label="Fairvero for business">
        <span className={s.brand}>
          <img src="/brand/fairvero-logo.png" alt="Fairvero" width={126} height={32} />
          <small>Business</small>
        </span>
        <h2 className={s.headline}>Earn trust in public.</h2>
        <ul className={s.benefits}>
          {BENEFITS.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      </section>
      <div className={s.card}>
        <h1>{title}</h1>
        {children}
      </div>
    </main>
  );
}

const safeNext = (n: string | null) => (n?.startsWith('/') && !n.startsWith('//') ? n : '/');

// Keeps where the person was going (e.g. claiming a business from the website) when they switch
// between log in and sign up.
const withNext = (path: string, next: string) =>
  next === '/' ? path : `${path}?next=${encodeURIComponent(next)}`;

// Google/Apple: the provider's SDK returns an ID token, the API verifies it and starts a session.
function useSocialSignIn(
  next: string,
  setError: (e: unknown) => void,
  onMfaRequired: (challenge: string) => void,
) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return async (provider: SocialProvider, idToken: string, displayName?: string) => {
    try {
      setError(null);
      const result = await authService.socialLogin(provider, idToken, displayName);
      if ('mfaRequired' in result) {
        onMfaRequired(result.challenge);
        return;
      }
      await qc.invalidateQueries({ queryKey: sessionKey });
      await navigate(next, { replace: true });
    } catch (err) {
      setError(err);
    }
  };
}

const LoginSchema = z.object({
  email: z.email('Enter a valid email'),
  password: z.string().min(1, 'Enter your password'),
});
const SignupSchema = z.object({
  displayName: z.string().trim().min(2, 'Enter your name'),
  email: z.email('Enter a valid email'),
  password: z.string().min(10, 'Use at least 10 characters'),
});

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const session = useSession();
  const [error, setError] = useState<unknown>(null);
  // Set when the password was right but a two-step code is still needed.
  const location = useLocation();
  const [challenge, setChallenge] = useState<string | null>(
    (location.state as { mfaChallenge?: string } | null)?.mfaChallenge ?? null,
  );
  const { register, handleSubmit, formState } = useForm({ resolver: zodResolver(LoginSchema) });
  const onSocial = useSocialSignIn(safeNext(params.get('next')), setError, setChallenge);
  if (session.data) return <Navigate to={safeNext(params.get('next'))} replace />;
  if (challenge)
    return (
      <AuthCard title="Two-step verification">
        <MfaLoginStep
          challenge={challenge}
          onSignedIn={async () => {
            await qc.invalidateQueries({ queryKey: sessionKey });
            await navigate(safeNext(params.get('next')), { replace: true });
          }}
          onCancel={() => {
            setChallenge(null);
          }}
        />
      </AuthCard>
    );
  return (
    <AuthCard title="Log in">
      <SocialSignIn mode="login" onToken={onSocial} />
      <form
        className={ui.stack}
        onSubmit={handleSubmit(async (v) => {
          try {
            setError(null);
            const result = await authService.login(v.email, v.password);
            if ('mfaRequired' in result) {
              setChallenge(result.challenge);
              return;
            }
            await qc.invalidateQueries({ queryKey: sessionKey });
            await navigate(safeNext(params.get('next')), { replace: true });
          } catch (err) {
            setError(err);
          }
        })}
      >
        <div className={ui.field}>
          <label htmlFor="email">Work email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className={ui.input}
            {...register('email')}
          />
          {formState.errors.email ? (
            <p className={ui.error}>{formState.errors.email.message}</p>
          ) : null}
        </div>
        <div className={ui.field}>
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            className={ui.input}
            {...register('password')}
          />
          {formState.errors.password ? (
            <p className={ui.error}>{formState.errors.password.message}</p>
          ) : null}
        </div>
        <ErrorNote error={error} />
        <Button type="submit" disabled={formState.isSubmitting}>
          Log in
        </Button>
      </form>
      <div className={ui.row}>
        <Link to="/forgot-password">Forgot password?</Link>
        <Link to={withNext('/signup', safeNext(params.get('next')))}>
          Create a business account
        </Link>
      </div>
    </AuthCard>
  );
}

export function SignupPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  // New accounts go on to add their business, unless they came to claim a specific one.
  const afterSignup = next === '/' ? '/onboarding' : next;
  const [error, setError] = useState<unknown>(null);
  const { register, handleSubmit, formState } = useForm({ resolver: zodResolver(SignupSchema) });
  // An existing account with two-step verification continues on the login page's code step.
  const onSocial = useSocialSignIn(afterSignup, setError, (challenge) => {
    void navigate('/login', { state: { mfaChallenge: challenge } });
  });
  return (
    <AuthCard title="Create your business account">
      <SocialSignIn mode="signup" onToken={onSocial} />
      <form
        className={ui.stack}
        onSubmit={handleSubmit(async (v) => {
          try {
            setError(null);
            await authService.register(v);
            await qc.invalidateQueries({ queryKey: sessionKey });
            await navigate(afterSignup, { replace: true });
          } catch (err) {
            setError(err);
          }
        })}
      >
        <div className={ui.field}>
          <label htmlFor="displayName">Your name</label>
          <input
            id="displayName"
            autoComplete="name"
            className={ui.input}
            {...register('displayName')}
          />
          {formState.errors.displayName ? (
            <p className={ui.error}>{formState.errors.displayName.message}</p>
          ) : null}
        </div>
        <div className={ui.field}>
          <label htmlFor="email">Work email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className={ui.input}
            {...register('email')}
          />
          <span className={ui.hint}>
            Using an address at your company&apos;s domain lets us verify your business instantly.
          </span>
          {formState.errors.email ? (
            <p className={ui.error}>{formState.errors.email.message}</p>
          ) : null}
        </div>
        <div className={ui.field}>
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            className={ui.input}
            {...register('password')}
          />
          {formState.errors.password ? (
            <p className={ui.error}>{formState.errors.password.message}</p>
          ) : null}
        </div>
        <ErrorNote error={error} />
        <p className={ui.hint}>
          By creating an account you agree to the{' '}
          <a href={`${env.publicWebUrl}/legal/terms`} target="_blank" rel="noreferrer">
            Terms of use
          </a>{' '}
          and the{' '}
          <a href={`${env.publicWebUrl}/legal/privacy`} target="_blank" rel="noreferrer">
            Privacy policy
          </a>
          .
        </p>
        <Button type="submit" disabled={formState.isSubmitting}>
          Create account
        </Button>
      </form>
      <Link to={withNext('/login', next)}>I already have an account</Link>
    </AuthCard>
  );
}

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const { register, handleSubmit } = useForm<{ email: string }>();
  return (
    <AuthCard title="Reset your password">
      {sent ? (
        <p>If an account exists for that address, we&apos;ve sent a reset link.</p>
      ) : (
        <form
          className={ui.stack}
          onSubmit={handleSubmit(async (v) => {
            await authService.forgotPassword(v.email).catch(() => undefined);
            setSent(true);
          })}
        >
          <div className={ui.field}>
            <label htmlFor="email">Email</label>
            <input id="email" type="email" required className={ui.input} {...register('email')} />
          </div>
          <Button type="submit">Send reset link</Button>
        </form>
      )}
      <Link to="/login">Back to log in</Link>
    </AuthCard>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const { register, handleSubmit } = useForm<{ password: string }>();
  return (
    <AuthCard title="Choose a new password">
      {done ? (
        <p>
          Password updated. <Link to="/login">Log in</Link>
        </p>
      ) : (
        <form
          className={ui.stack}
          onSubmit={handleSubmit(async (v) => {
            try {
              await authService.resetPassword(params.get('token') ?? '', v.password);
              setDone(true);
            } catch (err) {
              setError(err);
            }
          })}
        >
          <div className={ui.field}>
            <label htmlFor="password">New password</label>
            <input
              id="password"
              type="password"
              minLength={10}
              required
              autoComplete="new-password"
              className={ui.input}
              {...register('password')}
            />
          </div>
          <ErrorNote error={error} />
          <Button type="submit">Save password</Button>
        </form>
      )}
    </AuthCard>
  );
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const [state, setState] = useState<unknown>('pending');
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    authService.verifyEmail(params.get('token') ?? '').then(
      () => {
        setState('ok');
      },
      (err: unknown) => {
        setState(err);
      },
    );
  }, [params]);
  return (
    <AuthCard title="Confirm your email">
      {state === 'pending' ? (
        <Spinner />
      ) : state === 'ok' ? (
        <p>
          Email confirmed. <Link to="/">Go to your dashboard</Link>
        </p>
      ) : (
        <ErrorNote error={state} />
      )}
    </AuthCard>
  );
}

export function ConfirmEmailChangePage() {
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const token = params.get('token');
  const [result, setResult] = useState<{ email?: string; error?: unknown }>({});
  // The link works once; React strict mode would otherwise send it twice.
  const started = useRef(false);
  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    authService.confirmEmailChange(token).then(
      async (r) => {
        setResult({ email: r.email });
        await qc.invalidateQueries({ queryKey: sessionKey });
      },
      (error: unknown) => {
        setResult({ error });
      },
    );
  }, [token, qc]);
  return (
    <AuthCard title="Confirm your new email">
      {!token ? <p className={ui.error}>This link is missing its token.</p> : null}
      {token && !result.email && !result.error ? <Spinner label="Confirming" /> : null}
      <ErrorNote error={result.error} />
      {result.email ? (
        <p>
          Your email is now <strong>{result.email}</strong>.{' '}
          <Link to="/account">Go to my account</Link>
        </p>
      ) : null}
    </AuthCard>
  );
}
